import {mergeAttributes, Node} from "@tiptap/core";
import type {Node as ProseMirrorNode} from "@tiptap/pm/model";
import {ReactNodeViewRenderer} from "@tiptap/react";
import PrintoutContainerNodeView from "./PrintoutContainerNodeView";
import {clampPrintoutContainerColumns, normalizePrintoutContainerSpaceAbove} from "./printoutContainerOptions";

export const PrintoutContainerColumnNode = Node.create({
    name: "printoutContainerColumn",

    content: "block+",
    defining: true,
    isolating: true,
    selectable: false,

    parseHTML() {
        return [
            {
                tag: 'div[data-type="printout-container-column"]',
            },
        ];
    },

    renderHTML({HTMLAttributes}) {
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-type": "printout-container-column",
                class: "printout-container-column",
            }),
            0,
        ];
    },
});

// Shrinking merges removed-column content into the final retained column.
export const createResizedPrintoutContainerNode = (
    node: ProseMirrorNode,
    nextCount: number,
): ProseMirrorNode => {
    const schema = node.type.schema;
    const columnType = schema.nodes.printoutContainerColumn;
    const paragraphType = schema.nodes.paragraph;
    const safeCount = clampPrintoutContainerColumns(nextCount);
    const columns: ProseMirrorNode[] = [];

    for (let columnIndex = 0; columnIndex < safeCount; columnIndex += 1) {
        const isLastKeptColumn = columnIndex === safeCount - 1;
        const existingColumn = columnIndex < node.childCount ? node.child(columnIndex) : null;

        if (!isLastKeptColumn || node.childCount <= safeCount) {
            columns.push(existingColumn ?? columnType.create(null, paragraphType.create()));
            continue;
        }

        const mergedContent: ProseMirrorNode[] = [];

        for (let mergeIndex = columnIndex; mergeIndex < node.childCount; mergeIndex += 1) {
            node.child(mergeIndex).forEach((childBlock) => {
                mergedContent.push(childBlock);
            });
        }

        columns.push(columnType.create(null, mergedContent));
    }

    return node.type.create(node.attrs, columns, node.marks);
};

export const PrintoutContainerNode = Node.create({
    name: "printoutContainer",

    group: "block",
    content: "printoutContainerColumn+",
    selectable: true,
    draggable: true,
    isolating: true,

    addAttributes() {
        return {
            columnSeparators: {
                default: false,
                parseHTML: (element: HTMLElement) => element.getAttribute("data-printout-container-separators") === "true",
                renderHTML: (attrs: Record<string, unknown>) => ({
                    "data-printout-container-separators": String(attrs.columnSeparators === true),
                }),
            },
            spaceAboveMm: {
                default: 0,
                parseHTML: (element: HTMLElement) => normalizePrintoutContainerSpaceAbove(
                    element.getAttribute("data-printout-container-space-above"),
                ),
                renderHTML: (attrs: Record<string, unknown>) => ({
                    "data-printout-container-space-above": String(normalizePrintoutContainerSpaceAbove(attrs.spaceAboveMm)),
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="printout-container"]',
            },
        ];
    },

    renderHTML({HTMLAttributes}) {
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-type": "printout-container",
                class: "printout-rendered-container",
            }),
            0,
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(PrintoutContainerNodeView, {
            trackNodeViewPosition: true,
        });
    },
});
